import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Flag } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../firebase';
import { taskService } from '../../services/taskService';
import DeviationPicker from './DeviationPicker';
import { riskCalculationService } from '../../services/riskCalculationService';
import './ExecutionTaskExecutionView.css';

/**
 * Simplified task execution screen (PRD: execution_account_prd).
 * Flat steps/actions, tap to report deviation, Finalize → submitTaskCompletion.
 */
export default function ExecutionTaskExecutionView({ task: initialTask, userId, onBack }) {
  const { t } = useTranslation(['execution', 'tasks']);
  const [task, setTask] = useState(initialTask);
  const [deviations, setDeviations] = useState([]);
  const [pickerItem, setPickerItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [showFinalizeButton, setShowFinalizeButton] = useState(false); // true after "Pornește" or "Continuă" this session
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);
  const startedAtRef = useRef(null);

  const needsStart =
    task?.status === 'pending_assignment' ||
    task?.status === 'assigned' ||
    task?.status === 'acknowledged';

  useEffect(() => {
    if (task?.actualStart) {
      startedAtRef.current = task.actualStart;
    }
  }, [task?.actualStart]);

  const handleStartOrContinue = async () => {
    if (!task?.id || !userId || starting) return;
    setStarting(true);
    setError(null);
    try {
      if (task.status === 'pending_assignment') {
        await taskService.acknowledgeTask(task.id, userId);
      }
      if (needsStart) {
        await taskService.startTask(task.id, userId);
      }
      const updated = await taskService.getTask(task.id);
      setTask(updated);
      startedAtRef.current = updated?.actualStart ?? null;
      setShowFinalizeButton(true); // after start, show Finalizează
    } catch (e) {
      if (e?.code !== 'permission-denied') console.warn('Start task error', e);
      setError(e.message);
    } finally {
      setStarting(false);
    }
  };

  const protocol = task?.protocols?.[0];
  const steps = protocol?.steps || [];
  const riskLevel = task?.risk_data?.max_rpn != null
    ? riskCalculationService.getRiskLevel(task.risk_data.max_rpn)
    : 'medium';
  const locationLabel = task?.context?.roomName || task?.context?.departmentName
    ? [task.context.departmentName, task.context.roomName].filter(Boolean).join(' → ')
    : t('tasks:notSpecified', 'Nespecificat');

  const getDeviation = (itemId) => deviations.find((d) => d.itemId === itemId);
  const toggleOrOpenPicker = (item) => {
    const existing = getDeviation(item.id);
    if (existing) {
      setDeviations((prev) => prev.filter((d) => d.itemId !== item.id));
      return;
    }
    setPickerItem(item);
  };

  const handleDeviationSelect = (payload) => {
    setDeviations((prev) => [...prev, { ...payload, reportedAt: new Date() }]);
    setPickerItem(null);
  };

  const handleFinalize = async () => {
    if (!task?.id || !userId) return;
    setSubmitting(true);
    setError(null);
    try {
      const functions = getFunctions(app, 'us-central1');
      const submitTaskCompletion = httpsCallable(functions, 'submitTaskCompletion');
      const completedAt = new Date();
      const completedAtMs = completedAt.getTime();
      let startedAtMs =
        (() => {
          const startedAt = startedAtRef.current ?? task?.actualStart;
          if (typeof startedAt?.toMillis === 'function') return startedAt.toMillis();
          if (typeof startedAt === 'number') return startedAt;
          return completedAtMs - 60000;
        })();
      const maxStartMs = completedAtMs - 24 * 60 * 60 * 1000; // don't allow > 24h duration
      if (startedAtMs < maxStartMs) startedAtMs = maxStartMs;
      const payload = {
        taskId: task.id,
        startedAt: startedAtMs,
        completedAt: completedAt.getTime(),
        locationConfirmed: false,
        deviations: deviations.map((d) => ({
          itemId: d.itemId,
          itemType: d.itemType,
          itemName: d.itemName,
          reasonId: d.reasonId,
          reasonLabel: d.reasonLabel,
          reportedAt: d.reportedAt?.getTime?.() ?? Date.now(),
        })),
      };
      await submitTaskCompletion(payload);
      onBack();
    } catch (e) {
      console.error('Submit completion error', e);
      setError(e.message || t('errorSubmitting', 'Error submitting. Try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!task) return null;

  return (
    <div className="execution-exec-view">
      <header className="execution-exec-header">
        <button type="button" className="execution-exec-back" onClick={onBack} aria-label={t('back', 'Back')}>
          <ArrowLeft size={24} />
        </button>
        <div className="execution-exec-header-content">
          <span className={`execution-exec-risk risk-${riskLevel}`}>
            {riskLevel === 'critical' ? t('tasks:criticalRisk', 'RISC CRITIC') : riskLevel === 'high' ? t('tasks:highRisk', 'Risc ridicat') : t('tasks:mediumRisk', 'Risc mediu')}
          </span>
          <span className="execution-exec-location">{locationLabel}</span>
          <h1 className="execution-exec-protocol-name">
            {task.name || protocol?.protocolName || task.id}
          </h1>
          <div className="execution-exec-chips">
            <span>{task.context?.estimatedDuration ?? 15} min</span>
            {deviations.length > 0 && (
              <span className="execution-exec-deviation-badge">⚠️ {deviations.length}</span>
            )}
          </div>
        </div>
      </header>

      <p className="execution-exec-hint">
        {t('tapStepToReportProblem', 'Atinge un pas pentru a raporta o problemă')}
      </p>

      <div className="execution-exec-protocol-ref">
        {steps.map((step) => (
          <div key={step.stepId || step.id || step.stepNumber} className="execution-exec-step-block">
            <div
              className={`execution-exec-step-row ${getDeviation(step.stepId || step.id) ? 'deviated' : ''}`}
              onClick={() => toggleOrOpenPicker({ id: step.stepId || step.id, type: 'step', name: step.stepName || step.name || 'Step' })}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && toggleOrOpenPicker({ id: step.stepId || step.id, type: 'step', name: step.stepName || step.name || 'Step' })}
            >
              <span className="execution-exec-step-name">{step.stepName || step.name}</span>
              <span className="execution-exec-step-flag">
                {getDeviation(step.stepId || step.id) ? '⚠️' : <Flag size={16} />}
              </span>
            </div>
            {(step.actions || []).map((action, actionIndex) => {
              const equipment = action.requiredEquipment ?? action.equipment ?? [];
              const supplies = action.requiredSupplies ?? action.supplies ?? [];
              const equipmentList = Array.isArray(equipment) ? equipment : [];
              const suppliesList = Array.isArray(supplies) ? supplies : [];
              const eqLabels = equipmentList.map((e) => (typeof e === 'string' ? e : e?.name)).filter(Boolean);
              const supLabels = suppliesList.map((s) => (typeof s === 'string' ? s : s?.name)).filter(Boolean);
              return (
                <div key={action.actionId || action.id} className="execution-exec-action-row">
                  <span className="execution-exec-action-num">{actionIndex + 1}.</span>
                  <div className="execution-exec-action-content">
                    <span className="execution-exec-action-name">{action.actionName || action.name}</span>
                    {(eqLabels.length > 0 || supLabels.length > 0) && (
                      <div className="execution-exec-action-resources">
                        {eqLabels.length > 0 && (
                          <div className="execution-exec-resource-group">
                            <span className="execution-exec-resource-label">{t('equipment', 'Echipament')}</span>
                            <div className="execution-exec-action-chips">
                              {eqLabels.map((label, i) => (
                                <span key={`eq-${i}`} className="execution-exec-chip execution-exec-equipment-chip">{label}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {supLabels.length > 0 && (
                          <div className="execution-exec-resource-group">
                            <span className="execution-exec-resource-label">{t('supplies', 'Materiale')}</span>
                            <div className="execution-exec-action-chips">
                              {supLabels.map((label, i) => (
                                <span key={`sup-${i}`} className="execution-exec-chip execution-exec-supplies-chip">{label}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {error && <p className="execution-exec-error">{error}</p>}

      <div className="execution-exec-footer">
        {deviations.length > 0 && (
          <p className="execution-exec-deviations-count">⚠️ {deviations.length} {t('deviationsReported', 'devieri raportate')}</p>
        )}
        {needsStart ? (
          <button
            type="button"
            className="btn execution-exec-finalize-btn execution-exec-start-btn"
            onClick={handleStartOrContinue}
            disabled={starting}
          >
            {starting ? t('saving', 'Se salvează...') : t('startTask', 'Pornește sarcina →')}
          </button>
        ) : !showFinalizeButton ? (
          <button
            type="button"
            className="btn execution-exec-finalize-btn execution-exec-continue-btn"
            onClick={() => setShowFinalizeButton(true)}
          >
            {t('continueTask', 'Continuă sarcina →')}
          </button>
        ) : (
          <button
            type="button"
            className="btn execution-exec-finalize-btn"
            onClick={() => setShowFinalizeConfirm(true)}
            disabled={submitting}
          >
            {submitting ? t('saving', 'Se salvează...') : t('finalize', 'Finalizează ✓')}
          </button>
        )}
      </div>

      {showFinalizeConfirm && (
        <div className="execution-exec-finalize-overlay" onClick={() => setShowFinalizeConfirm(false)} role="dialog" aria-modal="true" aria-labelledby="finalize-confirm-title">
          <div className="execution-exec-finalize-confirm" onClick={(e) => e.stopPropagation()}>
            <h2 id="finalize-confirm-title" className="execution-exec-finalize-confirm-question">
              {t('readyQuestion', 'Gata?')}
            </h2>
            <div className="execution-exec-finalize-confirm-actions">
              <button
                type="button"
                className="btn execution-exec-finalize-confirm-btn execution-exec-finalize-confirm-no"
                onClick={() => setShowFinalizeConfirm(false)}
              >
                {t('no', 'Nu')}
              </button>
              <button
                type="button"
                className="btn execution-exec-finalize-confirm-btn execution-exec-finalize-confirm-yes"
                onClick={async () => {
                  setShowFinalizeConfirm(false);
                  await handleFinalize();
                }}
                disabled={submitting}
              >
                {submitting ? t('saving', 'Se salvează...') : t('yes', 'Da')}
              </button>
            </div>
          </div>
        </div>
      )}

      {pickerItem && (
        <DeviationPicker
          itemId={pickerItem.id}
          itemType={pickerItem.type}
          itemName={pickerItem.name}
          onSelect={handleDeviationSelect}
          onCancel={() => setPickerItem(null)}
        />
      )}
    </div>
  );
}
